import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Carousel, Image } from 'react-bootstrap';

import brandLogo from '../assets/brandLogo.png';

/* ── Carousel images ─────────────────────────────────────────────── */
import car1 from '../assets/030dadd9-1b96-4e5d-8aba-581d4c814565-removebg-preview.png';
import car2 from '../assets/1D29430B-3309-4ED9-A617-504A2C25BC63__1_-removebg-preview.png';
import car3 from '../assets/6286BB42-F1C0-4A3B-B534-C7D544B44064-removebg-preview.png';
import car4 from '../assets/7114f42f-b81f-45cf-bca4-d443db9ed9d8-removebg-preview.png';
import car5 from '../assets/75D78A05-8F85-4F9F-BA23-F2DEE1780149-removebg-preview.png';
import car6 from '../assets/Anna.Fiedorowicz.2025-778x1024-removebg-preview.png';
import car7 from '../assets/D285EF48-BD7C-4843-943C-EDA7C9F6388F-removebg-preview.png';
import car8 from '../assets/DFA0FE49-A774-4626-9086-33C64FF02B64-removebg-preview.png';
import car9 from '../assets/FB231863-A7CD-4F96-A828-AAA3AF4F670E-removebg-preview.png';
import car10 from '../assets/Screenshot_2025-12-18_at_18.47.35-removebg-preview.png';
import car11 from '../assets/Screenshot_2026-02-06_at_14.13.52-removebg-preview.png';
import car12 from '../assets/Screenshot_2026-02-10_at_11.29.31-removebg-preview.png';

import './Home.scss';

/* ── Data ─────────────────────────────────────────────────────── */

const carouselImages = [
  car1, car2, car3, car4, car5, car6, car7, car8, car9,
  car10, car11, car12,
];

const stats = [
  { value: '200+', label: 'Athletes', icon: 'fa-users' },
  { value: '50+', label: 'Universities', icon: 'fa-university' },
  { value: '9', label: 'Sports', icon: 'fa-trophy' },
  { value: '100%', label: 'Free for Coaches', icon: 'fa-check-circle' },
];

const sports = [
  { name: 'Volleyball', icon: 'fa-volleyball-ball', color: '#f59e0b' },
  { name: 'Beach Volleyball', icon: 'fa-volleyball-ball', color: '#f97316' },
  { name: 'Soccer', icon: 'fa-futbol', color: '#22c55e' },
  { name: 'Swimming', icon: 'fa-swimmer', color: '#3b82f6' },
  { name: 'Tennis', icon: 'fa-table-tennis', color: '#a855f7' },
  { name: 'Basketball', icon: 'fa-basketball-ball', color: '#ef4444' },
  { name: 'Track & Field', icon: 'fa-running', color: '#14b8a6' },
  { name: 'Ice Hockey', icon: 'fa-hockey-puck', color: '#0ea5e9' },
  { name: 'Water Polo', icon: 'fa-swimmer', color: '#0891b2' },
];

const steps = [
  {
    number: '01',
    icon: 'fa-user-plus',
    title: 'Create Free Account',
    description: 'Sign up as a coach in under 2 minutes. No credit card, no fees — ever.',
  },
  {
    number: '02',
    icon: 'fa-search',
    title: 'Browse & Filter Athletes',
    description: 'Use smart filters to find athletes by sport, position, graduation year, and more.',
  },
  {
    number: '03',
    icon: 'fa-envelope',
    title: 'Contact Directly',
    description: 'Found your next recruit? Reach out directly through our secure contact system.',
  },
];

const features = [
  { icon: 'fa-filter', title: 'Smart Filters', desc: 'Filter by sport, position, year, gender, GPA, and more.' },
  { icon: 'fa-video', title: 'Video Highlights', desc: 'Watch gameplay footage for every athlete on the platform.' },
  { icon: 'fa-columns', title: 'Compare Players', desc: 'Evaluate athletes side by side — stats, academics, highlights.' },
  { icon: 'fa-heart', title: 'Favorites List', desc: 'Build your shortlist and track athletes you\'re interested in.' },
  { icon: 'fa-bookmark', title: 'Saved Searches', desc: 'Save filter combos and reload them with a single click.' },
  { icon: 'fa-magic', title: 'Smart Recommendations', desc: 'Get personalized suggestions based on your activity.' },
];

const testimonials = [
  {
    quote: 'PlaySportUSA made it incredibly easy to find international talent that fits our program. The platform saved us hours of research.',
    role: 'NCAA Division I Coach',
    sport: 'Volleyball',
  },
  {
    quote: 'The quality of athletes is outstanding. Every profile is thoroughly vetted with videos and stats ready to evaluate.',
    role: 'NCAA Division II Coach',
    sport: 'Soccer',
  },
  {
    quote: 'The compare feature is a game-changer. Being able to view multiple athletes side by side made our recruiting decisions much faster.',
    role: 'NCAA Division I Coach',
    sport: 'Swimming',
  },
];

/* ── Animated counter hook ────────────────────────────────────── */

function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const num = parseInt(target, 10);
    if (isNaN(num)) { setCount(target); return; }
    let raf;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setCount(Math.floor(progress * num));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  return { count, ref };
}

/* ── Stat counter component ───────────────────────────────────── */

const StatCounter = ({ value, label, icon }) => {
  const numeric = parseInt(value, 10);
  const suffix = value.replace(/[0-9]/g, '');
  const { count, ref } = useCountUp(isNaN(numeric) ? value : String(numeric));

  return (
    <div className="landing__stat" ref={ref}>
      <div className="landing__stat-icon"><i className={`fas ${icon}`} /></div>
      <span className="landing__stat-value">
        {isNaN(numeric) ? value : `${count}${suffix}`}
      </span>
      <span className="landing__stat-label">{label}</span>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────────── */

const Home = () => {
  return (
    <div className="landing">

      {/* ════ Hero ════════════════════════════════════════════════ */}
      <section className="landing__hero">
        <div className="landing__hero-bg" />

        {/* Animated gradient orbs */}
        <div className="landing__orb-container" aria-hidden="true">
          {/* Main ring — rotates with conic gradient fill */}
          <div className="landing__ring landing__ring--outer">
            <div className="landing__ring-track" />
          </div>
          {/* Inner ring — rotates opposite direction */}
          <div className="landing__ring landing__ring--inner">
            <div className="landing__ring-track" />
          </div>
          {/* Glowing core orb */}
          <div className="landing__orb" />
          {/* Orbiting particles */}
          <div className="landing__particle landing__particle--1" />
          <div className="landing__particle landing__particle--2" />
          <div className="landing__particle landing__particle--3" />
        </div>

        <div className="landing__hero-content">
          <div className="landing__hero-badge">
            <i className="fas fa-globe-americas" /> International Recruiting Platform
          </div>
          <h1 className="landing__hero-title">
            Your Next Star Athlete<br />
            <span className="landing__hero-accent">is Playing Overseas</span>
          </h1>
          <p className="landing__hero-subtitle">
            Discover elite international athletes ready for NCAA competition.
            Search by sport, watch highlights, compare prospects, and contact
            players — all in one place, completely free for coaches.
          </p>
          <div className="landing__hero-ctas">
            <NavLink to="/create-account" className="landing__btn landing__btn--primary landing__btn--lg">
              <i className="fas fa-rocket" /> Get Started — It's Free
            </NavLink>
            <NavLink to="/sample-profile" className="landing__btn landing__btn--ghost landing__btn--lg">
              <i className="fas fa-eye" /> See Sample Profile
            </NavLink>
          </div>
          <p className="landing__hero-note">
            <i className="fas fa-check" /> No credit card required &nbsp;·&nbsp;
            <i className="fas fa-check" /> Free for coaches &nbsp;·&nbsp;
            <i className="fas fa-check" /> New athletes every week
          </p>
        </div>
        <div className="landing__hero-visual">
          <img src={brandLogo} alt="PlaySportUSA" className="landing__hero-logo" />
        </div>
      </section>

      {/* ════ Stats Bar ══════════════════════════════════════════ */}
      <section className="landing__stats">
        {stats.map((s, i) => (
          <StatCounter key={i} {...s} />
        ))}
      </section>

      {/* ════ Sports We Cover ════════════════════════════════════ */}
      <section className="landing__sports">
        <h2 className="landing__section-title">
          <span className="landing__title-line" />
          Sports We Cover
          <span className="landing__title-line" />
        </h2>
        <p className="landing__section-sub">
          From court to pool to rink, we scout the best international talent across 9 sports.
        </p>
        <div className="landing__sports-grid">
          {sports.map((sport, i) => (
            <div key={i} className="landing__sport-card" style={{ '--sport-color': sport.color }}>
              <div className="landing__sport-icon">
                <i className={`fas ${sport.icon}`} />
              </div>
              <span className="landing__sport-name">{sport.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ════ How It Works ═══════════════════════════════════════ */}
      <section className="landing__how">
        <h2 className="landing__section-title">
          <span className="landing__title-line" />
          How It Works
          <span className="landing__title-line" />
        </h2>
        <p className="landing__section-sub">
          From sign-up to your next recruit — in three simple steps.
        </p>
        <div className="landing__steps">
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              <div className="landing__step">
                <div className="landing__step-number">{step.number}</div>
                <div className="landing__step-icon">
                  <i className={`fas ${step.icon}`} />
                </div>
                <h3 className="landing__step-title">{step.title}</h3>
                <p className="landing__step-desc">{step.description}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="landing__step-connector">
                  <i className="fas fa-chevron-right" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ════ Features ═══════════════════════════════════════════ */}
      <section className="landing__features">
        <h2 className="landing__section-title">
          <span className="landing__title-line" />
          Built for Recruiting
          <span className="landing__title-line" />
        </h2>
        <p className="landing__section-sub">
          Powerful tools to help you find, evaluate, and recruit international athletes faster.
        </p>
        <div className="landing__features-grid">
          {features.map((f, i) => (
            <div key={i} className="landing__feature-card">
              <div className="landing__feature-icon">
                <i className={`fas ${f.icon}`} />
              </div>
              <h3 className="landing__feature-title">{f.title}</h3>
              <p className="landing__feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ════ Gallery ════════════════════════════════════════════ */}
      <section className="landing__gallery">
        <h2 className="landing__section-title">
          <span className="landing__title-line" />
          Our Athletes in Action
          <span className="landing__title-line" />
        </h2>
        <div className="landing__carousel-wrap">
          <Carousel
            nextIcon={<i className="fas fa-arrow-circle-right" />}
            prevIcon={<i className="fas fa-arrow-circle-left" />}
            interval={2500}
            variant="dark"
            className="landing__carousel"
          >
            {carouselImages.map((img, i) => (
              <Carousel.Item key={i}>
                <Image src={img} alt={`Athlete ${i + 1}`} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>
      </section>

      {/* ════ Testimonials ═══════════════════════════════════════ */}
      <section className="landing__testimonials">
        <h2 className="landing__section-title">
          <span className="landing__title-line" />
          What Coaches Say
          <span className="landing__title-line" />
        </h2>
        <div className="landing__testimonials-grid">
          {testimonials.map((t, i) => (
            <div key={i} className="landing__testimonial">
              <div className="landing__testimonial-quote">
                <i className="fas fa-quote-left" />
              </div>
              <p className="landing__testimonial-text">{t.quote}</p>
              <div className="landing__testimonial-author">
                <span className="landing__testimonial-role">{t.role}</span>
                <span className="landing__testimonial-sport">{t.sport}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ════ For Athletes ═══════════════════════════════════════ */}
      <section className="landing__athletes">
        <div className="landing__athletes-content">
          <div className="landing__athletes-badge">
            <i className="fas fa-graduation-cap" /> For Athletes
          </div>
          <h2 className="landing__athletes-title">
            Dream of Playing in the USA?
          </h2>
          <p className="landing__athletes-desc">
            PlaySportUSA gives talented international athletes direct exposure to American
            college coaches. Our scouts find the best prospects across Europe and build
            professional profiles that get noticed.
          </p>
          <ul className="landing__athletes-list">
            <li><i className="fas fa-check-circle" /> Professional profile with video highlights</li>
            <li><i className="fas fa-check-circle" /> Exposure to 500+ American universities</li>
            <li><i className="fas fa-check-circle" /> Evaluated by experienced scouts</li>
            <li><i className="fas fa-check-circle" /> Direct contact with college coaches</li>
          </ul>
          <a href="mailto:contact@playsportusa.com" className="landing__btn landing__btn--outline">
            <i className="fas fa-envelope" /> Contact Us to Learn More
          </a>
        </div>
      </section>

      {/* ════ Final CTA ══════════════════════════════════════════ */}
      <section className="landing__cta">
        <div className="landing__cta-inner">
          <h2 className="landing__cta-title">
            Ready to Find Your Next Recruit?
          </h2>
          <p className="landing__cta-sub">
            Join 50+ universities already using PlaySportUSA to discover international talent.
          </p>
          <div className="landing__cta-actions">
            <NavLink to="/create-account" className="landing__btn landing__btn--white landing__btn--lg">
              <i className="fas fa-arrow-right" /> Create Free Account
            </NavLink>
            <NavLink to="/available-players" className="landing__btn landing__btn--ghost-white landing__btn--lg">
              <i className="fas fa-users" /> Browse Athletes
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
